/**
 * Finding a pupil from the name typed at the front of a quick note.
 *
 * The quick-note convention is "{élève} {ce qu'il faut retenir}", typed in one
 * go on a tablet between two activities. Nothing about that text is reliable:
 * the name may be a first name, a surname, both, in either order; accents and
 * capitals are dropped as often as not; and a name typed at speed is a name
 * typed with a slip in it. Worse, the split point is not marked — "Léa Dupont
 * ne lit pas" and "Léa ne lit pas" start the same way.
 *
 * So no single split is assumed. Every plausible one is scored against every
 * pupil, and the caller gets the ranking rather than a verdict: the dialog
 * shows the runners-up so a wrong guess is one tap from being corrected,
 * which matters more here than being clever, because the note lands in a
 * child's file.
 */

import { studentDisplayName } from './student-roster.ts';

export interface MatchableStudent {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
}

export interface StudentMatch {
  studentId: string;
  /**
   * 0..1 confidence that this is the pupil the teacher meant, weighing both
   * how well the name was spelled and how much of the sentence it explains.
   */
  score: number;
  /** The words read as the name, exactly as typed. */
  matched: string;
  /** Everything after them — the observation itself. */
  rest: string;
}

/** How many leading words can be read as a name. Beyond that it is prose. */
const MAX_NAME_WORDS = 3;

/** Below this, a candidate is noise and is not offered at all. */
const MIN_SCORE = 0.6;

/** Under three characters, an approximate match is a coin toss. */
const MIN_FUZZY_LENGTH = 3;

/**
 * How much of the sentence a span explains, indexed by word count.
 *
 * A name read across two words is not marginally better than the same name
 * read across one — it is decisively better, because that second word was
 * typed and had to come from somewhere. Keeping the gap wide is what lets
 * `isAmbiguous` tell "Léa Dupont" from a class holding two Léas, where the
 * shared first name alone genuinely settles nothing.
 */
const SPAN_WEIGHT: readonly number[] = [0.86, 1, 1];

/** Combining accents, as NFD leaves them behind. */
const DIACRITICS = new RegExp('[\u0300-\u036f]', 'g');

/**
 * Comparison form: no accents, no case, no punctuation.
 *
 * Punctuation becoming a space is what makes "Léa :", "Léa," and "Jean-Luc"
 * all fall out correctly without a separate separator rule — the colon is
 * simply not there any more by the time anything is compared.
 */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Leading punctuation left behind once the name has been taken off the front. */
function stripLeadIn(value: string): string {
  return value.replace(/^[\s:;,.\-–—]+/, '').trim();
}

/** Levenshtein distance. Names are short, so the plain DP is fast enough. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = previous[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1);
      current[j] = Math.min(substitution, previous[j]! + 1, current[j - 1]! + 1);
    }
    previous = current;
  }

  return previous[b.length]!;
}

/**
 * Every spelling of a pupil's name a teacher might reach for.
 *
 * Both orders are included: a class list is usually surname-first, so
 * "Dupont Léa" is as natural to type as "Léa Dupont".
 */
function aliasesOf(student: MatchableStudent): string[] {
  // The class number in front of an imported name is never what gets typed.
  const name = fold(studentDisplayName(student.name));
  const first = fold(student.firstName);
  const last = fold(student.lastName);

  const aliases = new Set<string>();
  for (const alias of [name, first, last, `${first} ${last}`, `${last} ${first}`]) {
    const value = alias.trim();
    if (value) aliases.add(value);
  }

  // Records created before the name was split in two only carry `name`, so
  // its words are the only first name and surname available.
  if (!first && !last) {
    for (const word of name.split(' ')) if (word) aliases.add(word);
  }

  return [...aliases];
}

/** How well a typed fragment names a pupil; 0 when it plainly does not. */
function aliasScore(typed: string, alias: string): number {
  if (!typed || !alias) return 0;
  if (typed === alias) return 1;

  // "Mat" for "Mathieu": abbreviating is how a hurried teacher types.
  if (alias.startsWith(typed) && typed.length >= MIN_FUZZY_LENGTH) {
    return 0.75 + 0.2 * (typed.length / alias.length);
  }

  // A slip or two still identifies a name — but only once it is long enough
  // that the slip cannot have turned it into a different pupil entirely.
  if (typed.length < MIN_FUZZY_LENGTH || alias.length < MIN_FUZZY_LENGTH) return 0;
  const similarity = 1 - editDistance(typed, alias) / Math.max(typed.length, alias.length);

  // Kept below the prefix band on purpose: a guess is never as good as a
  // reading of what was actually typed.
  return similarity >= 0.7 ? similarity * 0.9 : 0;
}

/**
 * Pupils this text could be about, best first.
 *
 * Empty when nothing plausible was typed — which is the normal state while
 * the teacher is still on the first letter, not an error.
 */
export function matchStudentInText(
  text: string,
  students: readonly MatchableStudent[],
  limit = 4,
): StudentMatch[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length || !students.length) return [];

  const best = new Map<string, StudentMatch>();
  const aliases = students.map((student) => [student.id, aliasesOf(student)] as const);

  const seen = new Set<string>();
  const maxWords = Math.min(MAX_NAME_WORDS, words.length);

  for (let count = 1; count <= maxWords; count += 1) {
    const typed = fold(words.slice(0, count).join(' '));

    // Taking one more word changed nothing but punctuation ("Léa" then
    // "Léa :"), so the shorter span already covers this split.
    if (!typed || seen.has(typed)) continue;
    seen.add(typed);

    const span = SPAN_WEIGHT[count - 1] ?? 1;

    for (const [studentId, list] of aliases) {
      let raw = 0;
      for (const alias of list) raw = Math.max(raw, aliasScore(typed, alias));

      // Thresholded on the alias quality alone: whether this is a plausible
      // name at all does not depend on how much of the sentence it covers.
      if (raw < MIN_SCORE) continue;

      const score = raw * span;
      const previous = best.get(studentId);
      if (previous && previous.score >= score) continue;

      best.set(studentId, {
        studentId,
        score,
        matched: words.slice(0, count).join(' '),
        rest: stripLeadIn(words.slice(count).join(' ')),
      });
    }
  }

  return [...best.values()].sort((a, b) => b.score - a.score).slice(0, limit);
}

/**
 * True when the top two candidates are too close to pick between.
 *
 * Two pupils sharing a first name is the ordinary case, not the exotic one;
 * the dialog says so rather than filing the note under a guess.
 */
export function isAmbiguous(matches: readonly StudentMatch[]): boolean {
  return matches.length > 1 && matches[0]!.score - matches[1]!.score < 0.08;
}
